import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import type { Value } from 'react-phone-number-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { getDownloadURL, ref, uploadBytes, deleteObject } from 'firebase/storage';
import { auth, storage } from '../firebase/config';
import { FileType, FileUploadState, OnboardingData, OnboardingErrors, OnboardingStep } from './onboarding.type';
import {
  Timeframe,
  Compensation,
  Gender,
  ProjectRoles,
  ProjectStages,
  PTestAnswerType,
  RecentProject,
  UpdateUserData,
  UserRole,
  Verification,
} from '../user/user.type';
import { updateUserProfileInDatabase } from '../user/user.service';
import UserStore from '../user/user.store';
import PTestStore from './p-test.store';

const progressStart = OnboardingStep.PhoneNumber;
const progressEnd = OnboardingStep.UploadDocuments;

export default class OnboardingStore {
  formData: OnboardingData = {
    firstName: '',
    lastName: '',
    matchaPoints: 0,
    isProfileCompleted: false,
    hasVerifiedPhoneNumber: false,
    hasCompletedProfileInfo: false,
    hasCompletedLookingFor: false,
    hasCompletedProfessionalDescriptionPage: false,
    hasCompletedPTest: false,
    hasSubmittedDocumentsForVerification: false,
    profileImageUrl: '',
    phone: '',
    about: '',
    role: UserRole.SoftwareEngineer,
    gender: '',
    city: '',
    skills: [],
    notableCompanies: [],
    yearsOfExperience: 0,
    isStudent: false,
    degrees: [],
    target_role: undefined,
    project_stage: undefined,
    timeframe: null,
    compensation: null,
    longDescription: '',
    recentProject: {
      description: '',
      lastUpdated: new Date(),
      name: '',
      projectImage: '',
      techStack: [],
      developerTechStack: [],
      projectCategory: [],
    },
    verification: {
      linkedInProfileUrl: '',
      verificationCV: '',
      verificationCvDescription: '',
      verificationDocument: '',
      verificationDocumentDescription: '',
    },
    webOnboarding: {
      appliedFor: '',
      optedIn: false,
      meetingInviteSent: false,
      onboardingStep: OnboardingStep.PhoneNumber,
    },
    personality_results: null,
    continueable: false,
  };

  errors: OnboardingErrors = {};
  isSavingStep: boolean = false;
  documents: Record<FileType, FileUploadState> = {
    profileImage: this.createFileUploadState(),
    projectImage: this.createFileUploadState(),
    document: this.createFileUploadState(),
  };
  verificationDocuments: Map<string, FileUploadState> = new Map();

  userStore: UserStore;
  pTestStore: PTestStore;

  hasAttemptedCurrentStep: boolean = false;

  stepOrder = [
    OnboardingStep.PhoneNumber,
    OnboardingStep.PersonalInfo,
    OnboardingStep.RecentProject,
    OnboardingStep.Skills,
    OnboardingStep.RolePreferences,
    OnboardingStep.UploadDocuments,
  ];

  constructor(userStore: UserStore, pTestStore: PTestStore) {
    this.userStore = userStore;
    this.pTestStore = pTestStore;
    makeObservable(this, {
      step: computed,
      stepIndex: computed,
      progress: computed,
      nextStep: computed,
      formData: observable,
      errors: observable,
      isSavingStep: observable,
      documents: observable,
      verificationDocuments: observable,
      hasAttemptedCurrentStep: observable,

      // Computed properties
      isUploadingAnyDocument: computed,
      aboutCount: computed,
      projectDescriptionCount: computed,
      isValid: computed,
      continueable: computed,
      validateCurrentStep: computed,
      shouldShowStepErrors: computed,

      // Actions
      updateBooleanField: action,
      updateField: action,
      updateArrayField: action,
      updateProjectField: action,
      updateVerificationField: action,
      markWelcomeAgreementSeen: action,
      updateFounderAgreementStatus: action,
      updateProjectArrayField: action,
      updateDegreeField: action,
      updatePhoneNumber: action,
      initializeProjectFields: action,
      updatePTestResults: action,
      setError: action,
      setSavingStep: action,
      clearError: action,
      clearAllErrors: action,
      resetForm: action,
      initializeFromUser: action,
      uploadFile: action,
      updateOnboardingStep: action,
      markStepAttempted: action,
      saveCurrentStepData: action,
    });
  }

  private createFileUploadState(): FileUploadState {
    return {
      selectedFile: null,
      isUploading: false,
      uploadError: null,
    };
  }

  updateBooleanField = (field: keyof OnboardingData) => {
    if (field === 'isStudent') {
      this.formData.isStudent = !this.formData.isStudent;
    } else if (field === 'webOnboarding' && this.formData.role === UserRole.SoftwareEngineer) {
      this.ensureWebOnboardingObject();
      this.formData.webOnboarding!.optedIn = !this.formData.webOnboarding!.optedIn;
    }
    this.clearError(field as keyof OnboardingErrors);
  };

  updateField = (field: keyof OnboardingData, value: any) => {
    if (field === 'recentProject' || field === 'verification') {
      return;
    }

    (this.formData as any)[field] = value;

    // Clear errors for all fields when they're updated
    this.clearError(field as keyof OnboardingErrors);

    // Special validation for specific fields
    if (field === 'about' && typeof value === 'string' && value.length > 80) {
      this.formData.about = value.substring(0, 80);
    }
  };

  updateArrayField = (field: keyof OnboardingData, action: 'add' | 'remove', value: string) => {
    const currentArray = (this.formData[field] as string[]) || [];

    if (action === 'add' && !currentArray.includes(value)) {
      (this.formData[field] as string[]) = [...currentArray, value];
    } else if (action === 'remove') {
      (this.formData[field] as string[]) = currentArray.filter(item => item !== value);
    }

    this.clearError(field as keyof OnboardingErrors);
  };

  updateProjectField = (field: keyof RecentProject, value: any) => {
    if (field === 'techStack' || field === 'developerTechStack' || field === 'projectCategory') {
      return;
    }

    (this.formData.recentProject as any)[field] = value;
    this.formData.recentProject.lastUpdated = new Date();

    if (this.errors.recentProject) {
      const updatedErrors = { ...this.errors.recentProject };
      delete updatedErrors[field];

      if (Object.keys(updatedErrors).length === 0) {
        delete this.errors.recentProject;
      } else {
        this.errors.recentProject = updatedErrors;
      }
    }
  };

  updateProjectArrayField = (field: 'techStack' | 'developerTechStack' | 'projectCategory', action: 'add' | 'remove', value: string) => {
    const currentArray = this.formData.recentProject[field] || [];

    if (action === 'add' && !currentArray.includes(value)) {
      // Special logic for projectCategory (max 3 items)
      if (field === 'projectCategory' && currentArray.length >= 3) {
        return;
      }

      this.formData.recentProject[field] = [...currentArray, value] as any;
    } else if (action === 'remove') {
      this.formData.recentProject[field] = currentArray.filter(item => item !== value) as any;
    }

    this.formData.recentProject.lastUpdated = new Date();

    if (this.errors.recentProject?.[field]) {
      const updatedErrors = { ...this.errors.recentProject };
      delete updatedErrors[field];
      this.errors.recentProject = updatedErrors;
    }
  };

  updateVerificationField = (field: keyof Verification, value: string) => {
    this.ensureVerificationObject();
    (this.formData.verification as Verification)[field] = value;
    this.clearError(field as keyof OnboardingErrors);
  };

  markWelcomeAgreementSeen = () => {
    const user = this.userStore.user;
    if (user?.uid && !user.hasSeenWelcomeAgreement) {
      updateUserProfileInDatabase(user.uid, {
        hasSeenWelcomeAgreement: true,
      });

      this.userStore.updateUserData({
        hasSeenWelcomeAgreement: true,
      });
    }
  };

  updateFounderAgreementStatus = (hasAgreed: boolean) => {
    const user = this.userStore.user;
    if (user?.uid) {
      updateUserProfileInDatabase(user.uid, {
        hasSeenWelcomeAgreement: true,
        hasAgreedToProTerms: hasAgreed,
      });

      this.userStore.updateUserData({
        hasSeenWelcomeAgreement: true,
        hasAgreedToProTerms: hasAgreed,
      });
    }
  };

  updateDegreeField = (action: 'add' | 'remove', degree?: string, level?: string, title?: string, institution?: string) => {
    if (action === 'add' && level && title && institution) {
      const validation = this.validateEducationFields(level, title, institution);

      if (!validation.isValid) {
        this.setEducationFormError(validation.errors);

        const titleProvided = !!title.trim();
        const institutionProvided = !!institution.trim();
        const hasInvalidTitle = titleProvided && !this.isValidTextInput(title);
        const hasInvalidInstitution = institutionProvided && !this.isValidTextInput(institution);

        if (hasInvalidTitle || hasInvalidInstitution) {
          this.setError('degrees', 'Only letters and numbers are allowed');
        } else {
          const missingFields: string[] = [];
          if (validation.errors.level) missingFields.push('qualification level');
          if (!titleProvided) missingFields.push('field of study');
          if (!institutionProvided) missingFields.push('institution');
          if (missingFields.length) {
            this.setError('degrees', `Please provide: ${missingFields.join(', ')}`);
          }
        }
        return false;
      }

      this.clearEducationFormErrors();
      const degreeString = `${level} in ${title.trim()} from ${institution.trim()}`;

      if (this.formData.degrees.includes(degreeString)) {
        this.setError('degrees', 'This degree has already been added');
        return false;
      }

      this.formData.degrees.push(degreeString);
      return true;
    } else if (action === 'remove' && degree) {
      this.formData.degrees = this.formData.degrees.filter(d => d !== degree);
      this.clearError('degrees');
    }

    return false;
  };

  updatePhoneNumber = (value: Value) => {
    this.updateField('phone', value || '');
  };

  updatePTestResults = (answers: PTestAnswerType[]) => {
    if (!this.formData.personality_results) {
      this.formData.personality_results = {
        answers: [],
        personality_code: '',
        resilienceFlag: '',
        totalScore: 0,
      };
    }

    // Update p-test store with the answers first
    this.pTestStore.answers = [...answers];

    // Calculate all personality results using p-test store methods
    this.formData.personality_results.answers = [...this.pTestStore.answers];
    this.formData.personality_results.totalScore = this.pTestStore.calculateTotalScore();
    this.formData.personality_results.personality_code = this.pTestStore.calculatePersonalityCode(this.formData.role!);
    this.formData.personality_results.resilienceFlag = this.pTestStore.calculateResilienceFlag();
  };

  get aboutCount() {
    return this.formData.about.length;
  }

  get projectDescriptionCount() {
    return this.formData.recentProject.description.length;
  }

  get isValid() {
    return Object.keys(this.validateForm()).length === 0;
  }

  get isUploadingAnyDocument() {
    const regularDocsUploading = Object.values(this.documents).some(doc => doc.isUploading);
    const verificationDocsUploading = Array.from(this.verificationDocuments.values()).some(doc => doc.isUploading);
    return regularDocsUploading || verificationDocsUploading;
  }

  get step() {
    return this.formData.webOnboarding?.onboardingStep || OnboardingStep.PhoneNumber;
  }

  get stepIndex() {
    return this.stepOrder.indexOf(this.step);
  }

  get nextStep() {
    return this.stepOrder[this.stepIndex + 1] || null;
  }

  get previousStep() {
    return this.stepOrder[this.stepIndex - 1] || null;
  }

  get progress() {
    const startIndex = this.stepOrder.indexOf(progressStart);
    const endIndex = this.stepOrder.indexOf(progressEnd);
    const currentIndex = this.stepIndex;

    if (currentIndex > endIndex || currentIndex < startIndex) {
      return null;
    }

    return (currentIndex - startIndex + 1) / (endIndex - startIndex + 1);
  }

  get validateCurrentStep(): OnboardingErrors {
    const allErrors = this.validateForm();
    const stepErrors: OnboardingErrors = {};

    switch (this.step) {
    case OnboardingStep.PhoneNumber:
      if (allErrors.phone) {
        stepErrors.phone = allErrors.phone;
      }
      break;

    case OnboardingStep.PersonalInfo:
      if (allErrors.profileImageUrl) stepErrors.profileImageUrl = allErrors.profileImageUrl;
      if (allErrors.firstName) stepErrors.firstName = allErrors.firstName;
      if (allErrors.lastName) stepErrors.lastName = allErrors.lastName;
      if (allErrors.about) stepErrors.about = allErrors.about;
      // user role remove for now
      // if (allErrors.role) stepErrors.role = allErrors.role;
      if (allErrors.gender) stepErrors.gender = allErrors.gender;
      if (allErrors.city) stepErrors.city = allErrors.city;
      break;

    case OnboardingStep.RecentProject:
      if (allErrors.recentProject) {
        stepErrors.recentProject = allErrors.recentProject;
      }
      break;

    case OnboardingStep.Skills:
      if (allErrors.skills) {
        stepErrors.skills = allErrors.skills;
      }
      if (allErrors.degrees) {
        stepErrors.degrees = allErrors.degrees;
      }
      break;

    case OnboardingStep.RolePreferences:
      // Role-specific validation
      if (this.formData.role === UserRole.Founder) {
        if (allErrors.target_role) {
          stepErrors.target_role = allErrors.target_role;
        }
      } else if (this.formData.role === UserRole.SoftwareEngineer) {
        if (allErrors.project_stage) {
          stepErrors.project_stage = allErrors.project_stage;
        }
      }

      // Common validation for both roles
      if (allErrors.timeframe) {
        stepErrors.timeframe = allErrors.timeframe;
      }
      if (allErrors.compensation) {
        stepErrors.compensation = allErrors.compensation;
      }
      if (allErrors.longDescription) {
        stepErrors.longDescription = allErrors.longDescription;
      }
      break;

    case OnboardingStep.UploadDocuments:
      if (allErrors.linkedInProfileUrl) {
        stepErrors.linkedInProfileUrl = allErrors.linkedInProfileUrl;
      }
      if (allErrors.verificationCV) {
        stepErrors.verificationCV = allErrors.verificationCV;
      }
      if (allErrors.verificationCvDescription) {
        stepErrors.verificationCvDescription = allErrors.verificationCvDescription;
      }
      break;

    case OnboardingStep.OnboardingCompleted:
      // PTest validation - can be empty since it's typically auto-completed
      break;

    default:
      break;
    }

    return stepErrors;
  }

  get continueable() {
    const stepErrors = this.validateCurrentStep;
    return Object.keys(stepErrors).length === 0;
  }

  get shouldShowStepErrors(): OnboardingErrors {
    // Only show errors if user has attempted to continue on current step
    if (this.hasAttemptedCurrentStep) {
      return this.validateCurrentStep;
    }
    return {}; // Return empty errors object if no attempt made
  }

  getNextStep = (currentStep?: OnboardingStep): OnboardingStep => {
    const step = currentStep || this.step;
    const currentIndex = this.stepOrder.indexOf(step);

    if (currentIndex === -1) {
      return this.stepOrder[0];
    }

    if (currentIndex === this.stepOrder.length - 1) {
      return step;
    }

    return this.stepOrder[currentIndex + 1];
  };

  markStepAttempted = action(() => {
    this.hasAttemptedCurrentStep = true;
  });

  updateOnboardingStep = action((step: OnboardingStep) => {
    this.ensureWebOnboardingObject();
    if (this.formData.webOnboarding) {
      this.formData.webOnboarding.onboardingStep = step;
    }
    this.hasAttemptedCurrentStep = false;
  });

  setSavingStep = action((isSaving: boolean) => {
    this.isSavingStep = isSaving;
  });

  saveCurrentStepData = async (): Promise<boolean> => {
    this.setSavingStep(true);

    try {
      let userId = this.userStore.user?.uid;
      if (!userId && auth.currentUser) {
        userId = auth.currentUser.uid;
      }
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Upload documents if on UploadDocuments step (same as submitOnboarding did)
      if (this.step === OnboardingStep.UploadDocuments) {
        const documentsUploadSuccess = await this.uploadAllFiles();
        if (!documentsUploadSuccess) return false;
      }

      // Prepare partial update based on current step
      const partialUpdate = this.getPartialUpdateForCurrentStep();

      if (Object.keys(partialUpdate).length > 0) {
        await updateUserProfileInDatabase(userId, partialUpdate);
        // Update local user store with the new data
        this.userStore.updateUserData(partialUpdate);
      }

      return true;
    } catch (error) {
      return false;
    } finally {
      this.setSavingStep(false);
    }
  };

  private getPartialUpdateForCurrentStep = (): Partial<UpdateUserData> => {
    const update: Partial<UpdateUserData> = {};

    const currentStepErrors = this.validateCurrentStep;
    const isCurrentStepValid = Object.keys(currentStepErrors).length === 0;

    // Always ensure webOnboarding exists before updating onboardingStep
    if (!update.webOnboarding) {
      // For Software Engineers, include all fields; for Founders, omit them
      update.webOnboarding = this.formData.role === UserRole.SoftwareEngineer
        ? {
          appliedFor: this.formData.webOnboarding?.appliedFor || '',
          optedIn: this.formData.webOnboarding?.optedIn || false,
          meetingInviteSent: this.formData.webOnboarding?.meetingInviteSent || false,
        }
        : {};
    }

    if (isCurrentStepValid) {
      update.webOnboarding.onboardingStep = this.step;
    }

    switch (this.step) {
    case OnboardingStep.PhoneNumber:
      const phoneStepErrors = this.validateCurrentStep;
      const isPhoneValid = Object.keys(phoneStepErrors).length === 0;

      if (isPhoneValid) {
        update.phone = this.formData.phone;
        update.hasVerifiedPhoneNumber = true;
      }
      break;

    case OnboardingStep.PersonalInfo:
      const personalInfoStepErrors = this.validateCurrentStep;
      const isPersonalInfoValid = Object.keys(personalInfoStepErrors).length === 0;

      update.firstName = this.formData.firstName;
      update.lastName = this.formData.lastName;
      update.about = this.formData.about;
      update.role = this.formData.role as UserRole;
      update.gender = this.formData.gender as Gender;
      update.city = this.formData.city;
      update.profileImageUrl = this.formData.profileImageUrl;

      if (isPersonalInfoValid) {
        update.hasCompletedProfileInfo = true;

        const rolePoints = this.formData.role === UserRole.SoftwareEngineer ? 3 : 1;
        update.matchaPoints = (this.formData.matchaPoints || 0) + rolePoints;
      }
      break;

    case OnboardingStep.RecentProject:
      const recentProjectStepErrors = this.validateCurrentStep;
      const isRecentProjectValid = Object.keys(recentProjectStepErrors).length === 0;

      update.recentProject = {
        name: this.formData.recentProject.name,
        description: this.formData.recentProject.description,
        projectImage: this.formData.recentProject.projectImage,
        lastUpdated: new Date(),
        ...(this.formData.role === UserRole.SoftwareEngineer
          ? {
            techStack: this.formData.recentProject.techStack || [],
            developerTechStack: this.formData.recentProject.developerTechStack || [],
          }
          : { projectCategory: this.formData.recentProject.projectCategory || [] }
        ),
      };

      if (isRecentProjectValid) {
        update.isProfileCompleted = true;
      }

      break;

    case OnboardingStep.Skills:
      const skillsStepErrors = this.validateCurrentStep;
      const isSkillsValid = Object.keys(skillsStepErrors).length === 0;

      update.skills = this.formData.skills;
      update.degrees = this.formData.degrees;
      update.notableCompanies = this.formData.notableCompanies;
      update.yearsOfExperience = this.formData.yearsOfExperience;
      update.isStudent = this.formData.isStudent;

      if (isSkillsValid) {
        update.hasCompletedProfessionalDescriptionPage = true;
      }

      break;

    case OnboardingStep.RolePreferences:
      const rolePreferencesStepErrors = this.validateCurrentStep;
      const isRolePreferencesValid = Object.keys(rolePreferencesStepErrors).length === 0;

      // Update optedIn value only for engineers (Firebase doesn't allow undefined)
      if (this.formData.role === UserRole.SoftwareEngineer) {
          update.webOnboarding!.optedIn = this.formData.webOnboarding?.optedIn || false;
      }
      update.target_role = this.formData.target_role as ProjectRoles;
      update.project_stage = this.formData.project_stage as ProjectStages;
      update.timeframe = this.formData.timeframe as Timeframe;
      update.compensation = this.formData.compensation as Compensation;
      update.longDescription = this.formData.longDescription;

      if (isRolePreferencesValid) {
        update.hasCompletedLookingFor = true;
      }
      break;

    case OnboardingStep.UploadDocuments:
      const uploadDocumentsStepErrors = this.validateCurrentStep;
      const isUploadDocumentsValid = Object.keys(uploadDocumentsStepErrors).length === 0;

      update.verification = this.formData.verification || {
        linkedInProfileUrl: '',
        verificationCV: '',
        verificationCvDescription: '',
        verificationDocument: '',
        verificationDocumentDescription: '',
      };

      if (isUploadDocumentsValid) {
        update.hasSubmittedDocumentsForVerification = true;
      }
      break;

    case OnboardingStep.OnboardingCompleted:
      if (this.pTestStore.isComplete && this.formData.personality_results) {
        update.hasCompletedPTest = true;
      }
      break;

    default:
      break;
    }

    // Filter out undefined values
    return Object.fromEntries(
      Object.entries(update).filter(([_, value]) => value !== undefined),
    ) as Partial<UpdateUserData>;
  };

  setSelectedFile = (fileType: FileType, file: File | null) => {
    runInAction(() => {
      this.documents[fileType].selectedFile = file;
      this.documents[fileType].uploadError = null;
    });
  };

  setFileUploadError = (fileType: FileType, error: string | null) => {
    runInAction(() => {
      this.documents[fileType].uploadError = error;
    });
  };

  setVerificationDocumentError = (fileName: string, error: string | null) => {
    runInAction(() => {
      const docState = this.verificationDocuments.get(fileName);
      if (docState) {
        docState.uploadError = error;
      } else {
        // If the document doesn't exist yet, create it with just the error
        this.verificationDocuments.set(fileName, {
          selectedFile: null,
          isUploading: false,
          uploadError: error,
        });
      }
    });
  };

  setSelectedPrefabImage = async (imageUrl: string) => {
    // Delete old uploaded image if it exists before setting the default one
    await this.deleteOldFile('projectImage');

    runInAction(() => {
      this.formData.recentProject.projectImage = imageUrl;

      this.documents.projectImage.selectedFile = null;
      this.documents.projectImage.uploadError = null;
    });
  };

  addVerificationDocument = (fileName: string, file: File) => {
    runInAction(() => {
      this.verificationDocuments.set(fileName, {
        selectedFile: file,
        isUploading: false,
        uploadError: null,
      });
    });
  };

  setError = (field: keyof OnboardingErrors, message: string | Partial<Record<keyof RecentProject, string>>) => {
    runInAction(() => {
      this.errors = {
        ...this.errors,
        [field]: message as any,
      };
    });
  };

  clearError = (field: keyof OnboardingErrors) => {
    if (this.errors[field]) {
      delete this.errors[field];
      this.errors = { ...this.errors };
    }
  };

  clearAllErrors = () => {
    runInAction(() => {
      this.errors = {};
    });
  };

  // Helpers
  private ensureVerificationObject = () => {
    if (!this.formData.verification) {
      this.formData.verification = {
        linkedInProfileUrl: '',
        verificationCV: '',
        verificationCvDescription: '',
        verificationDocument: '',
        verificationDocumentDescription: '',
      };
    }
  };

  private ensureWebOnboardingObject = (jobRoleData?: any) => {
    if (!this.formData.webOnboarding) {
      this.formData.webOnboarding = this.formData.role === UserRole.SoftwareEngineer
        ? {
          appliedFor: jobRoleData?.hiringCompany || '',
          optedIn: false,
          meetingInviteSent: false,
          onboardingStep: OnboardingStep.PhoneNumber,
        }
        : {
          onboardingStep: OnboardingStep.PhoneNumber,
        };
    }
  };

  private isValidTextInput = (text: string): boolean => {
    const textPattern = new RegExp('^[a-zA-Z0-9\\s\\-\\.\']+$'); // Only letters, numbers, spaces and hyphens
    return textPattern.test(text.trim());
  };

  private isValidTextFieldInput = (text: string): boolean => {
    const textPattern = new RegExp(/^[a-zA-Z0-9\s,.!?;:()'"\-_@#$%&]+$/); // Allow common punctuation for text fields
    return textPattern.test(text.trim());
  };

  private validateEducationFields = (level: string, title: string, institution: string) => {
    const errors = {
      level: !level,
      title: !title.trim() || !this.isValidTextInput(title),
      institution: !institution.trim() || !this.isValidTextInput(institution),
    };

    return {
      isValid: !Object.values(errors).some(error => error),
      errors,
    };
  };

  private setEducationFormError = (errors: { level: boolean; title: boolean; institution: boolean; }) => {
    runInAction(() => {
      this.errors.educationForm = errors;
      this.errors = { ...this.errors };
    });
  };

  private clearEducationFormErrors = () => {
    runInAction(() => {
      if (this.errors.educationForm) {
        delete this.errors.educationForm;
        this.errors = { ...this.errors };
      }
      this.clearError('degrees');
    });
  };

  initializeFromUser = (jobRoleData?: any) => {
    const user = this.userStore.user;
    if (user) {
      this.formData.firstName = user.firstName || '';
      this.formData.lastName = user.lastName || '';
      this.formData.profileImageUrl = user.profileImageUrl || '';
      this.formData.phone = user.phone || '';
      this.formData.about = user.about || '';
      this.formData.role = user.role || '';
      this.formData.gender = user.gender || '';
      this.formData.city = user.city || '';
      this.formData.skills = user.skills || [];
      this.formData.notableCompanies = user.notableCompanies || [];
      this.formData.yearsOfExperience = user.yearsOfExperience || 0;
      this.formData.isStudent = user.isStudent || false;
      this.formData.degrees = user.degrees || [];
      this.formData.target_role = user.target_role || undefined;
      this.formData.project_stage = user.project_stage || undefined;
      this.formData.timeframe = user.timeframe || null;
      this.formData.compensation = user.compensation || null;
      this.formData.recentProject = {
        name: user.recentProject?.name || '',
        description: user.recentProject?.description || '',
        projectImage: user.recentProject?.projectImage || '',
        techStack: user.recentProject?.techStack || [],
        developerTechStack: user.recentProject?.developerTechStack || [],
        projectCategory: user.recentProject?.projectCategory || [],
        lastUpdated: user.recentProject?.lastUpdated || new Date(),
      };
      this.formData.verification = {
        linkedInProfileUrl: user.verification?.linkedInProfileUrl || '',
        verificationCV: user.verification?.verificationCV || '',
        verificationCvDescription: user.verification?.verificationCvDescription || '',
        verificationDocument: user.verification?.verificationDocument || '',
        verificationDocumentDescription: user.verification?.verificationDocumentDescription || '',
      };
      this.formData.webOnboarding = user.role === UserRole.SoftwareEngineer
        ? {
          appliedFor: user.webOnboarding?.appliedFor || jobRoleData?.hiringCompany || '',
          optedIn: user.webOnboarding?.optedIn || false,
          meetingInviteSent: user.webOnboarding?.meetingInviteSent || false,
          onboardingStep: user.webOnboarding?.onboardingStep || OnboardingStep.PhoneNumber,
        }
        : {
          onboardingStep: user.webOnboarding?.onboardingStep || OnboardingStep.PhoneNumber,
        };
      this.formData.personality_results = user.personality_results || null;
    }
  };

  initializeProjectFields = (role: UserRole) => {
    if (!this.formData.recentProject) {
      this.formData.recentProject = {
        name: '',
        description: '',
        projectImage: '',
        lastUpdated: new Date(),
        techStack: [],
        developerTechStack: [],
        projectCategory: [],
      };
    }

    // Initialize appropriate fields based on role
    if (role === UserRole.SoftwareEngineer) {
      this.formData.recentProject.techStack = this.formData.recentProject.techStack || [];
      this.formData.recentProject.developerTechStack = this.formData.recentProject.developerTechStack || [];
      this.formData.recentProject.projectCategory = undefined;
    } else {
      this.formData.recentProject.projectCategory = this.formData.recentProject.projectCategory || [];
      this.formData.recentProject.techStack = undefined;
      this.formData.recentProject.developerTechStack = undefined;
    }
  };

  resetForm = () => {
    this.formData = {
      firstName: '',
      lastName: '',
      matchaPoints: 0,
      isProfileCompleted: false,
      hasVerifiedPhoneNumber: false,
      hasCompletedProfileInfo: false,
      hasCompletedLookingFor: false,
      hasCompletedProfessionalDescriptionPage: false,
      hasCompletedPTest: false,
      hasSubmittedDocumentsForVerification: false,
      profileImageUrl: '',
      phone: '',
      about: '',
      role: '',
      gender: '',
      city: '',
      skills: [],
      notableCompanies: [],
      yearsOfExperience: 0,
      isStudent: false,
      degrees: [],
      target_role: undefined,
      project_stage: undefined,
      timeframe: null,
      compensation: null,
      longDescription: '',
      recentProject: {
        name: '',
        description: '',
        projectImage: '',
        techStack: [],
        developerTechStack: [],
        projectCategory: [],
        lastUpdated: new Date(),
      },
      verification: {
        linkedInProfileUrl: '',
        verificationCV: '',
        verificationCvDescription: '',
        verificationDocument: '',
        verificationDocumentDescription: '',
      },
      webOnboarding: {
        appliedFor: '',
        optedIn: false,
        meetingInviteSent: false,
        onboardingStep: OnboardingStep.PhoneNumber,
      },
      continueable: false,
    };
    this.errors = {};
    this.hasAttemptedCurrentStep = false;

    // Reset file upload states
    Object.keys(this.documents).forEach(fileType => {
      this.documents[fileType as FileType] = this.createFileUploadState();
    });
    this.verificationDocuments.clear();
  };

  private getStoragePath = (fileType: FileType, fileName: string): string => {
    let userId = this.userStore.user?.uid;
    if (!userId && auth.currentUser) {
      userId = auth.currentUser.uid;
    }
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const isTestMode = process.env.NODE_ENV === 'development';
    const basePath = isTestMode ? 'WebOnboardingTestUserImages' : 'userImages';

    const pathMap = {
      profileImage: `${basePath}/${userId}/profilePicture`,
      projectImage: `${basePath}/${userId}/projectPicture`,
      document: `${basePath}/${userId}/verification/${fileName}`,
    };

    return pathMap[fileType];
  };

  deleteOldFile = async (fileType: FileType): Promise<boolean> => {
    try {
      let currentFileUrl = '';

      if (fileType === 'profileImage') {
        currentFileUrl = this.formData.profileImageUrl;
      } else if (fileType === 'projectImage') {
        currentFileUrl = this.formData.recentProject.projectImage;
      }

      if (!currentFileUrl) {
        return true;
      }

      // Only delete user-uploaded files, not prefab images
      // User-uploaded files contain 'WebOnboardingTestUserImages' or 'userImages' in the path
      // Prefab images contain 'projectImages' in the path
      const isUserUploadedFile =
        currentFileUrl.includes('WebOnboardingTestUserImages')
        || currentFileUrl.includes('userImages');

      const isPrefabImage = currentFileUrl.includes('projectImages');

      if (isPrefabImage || !isUserUploadedFile) {
        return true;
      }

      // Extract file path from URL and delete from storage
      const urlParts = currentFileUrl.split('/o/')[1];
      if (urlParts) {
        const filePath = decodeURIComponent(urlParts.split('?')[0]);
        const fileRef = ref(storage, filePath);
        await deleteObject(fileRef);
      }

      return true;
    } catch (error) {
      // Don't fail the upload if deletion fails
      return true;
    }
  };

  deleteOldVerificationFile = async (fileName: string): Promise<boolean> => {
    try {
      let currentFileUrl = '';

      // Get current verification file URL
      if (fileName === 'verificationCV' && this.formData.verification?.verificationCV) {
        currentFileUrl = this.formData.verification.verificationCV;
      } else if (fileName === 'verificationDocument' && this.formData.verification?.verificationDocument) {
        currentFileUrl = this.formData.verification.verificationDocument;
      }

      if (!currentFileUrl) return true; // No file to delete

      // Extract file path from URL and delete from storage
      const urlParts = currentFileUrl.split('/o/')[1];
      if (urlParts) {
        const filePath = decodeURIComponent(urlParts.split('?')[0]);
        const fileRef = ref(storage, filePath);
        await deleteObject(fileRef);
      }

      return true;
    } catch (error) {
      // Don't fail the upload if deletion fails
      return true;
    }
  };

  uploadFile = async (fileType: FileType): Promise<string | null> => {
    const fileState = this.documents[fileType];
    if (!fileState.selectedFile) return null;

    runInAction(() => {
      fileState.isUploading = true;
      fileState.uploadError = null;
    });

    try {
      // Delete old file before uploading new one
      await this.deleteOldFile(fileType);

      const storagePath = this.getStoragePath(fileType, fileState.selectedFile.name);
      const fileRef = ref(storage, storagePath);
      const snapshot = await uploadBytes(fileRef, fileState.selectedFile);
      const downloadURL = await getDownloadURL(snapshot.ref);

      runInAction(() => {
        if (fileType === 'profileImage') {
          this.formData.profileImageUrl = downloadURL;
        } else if (fileType === 'projectImage') {
          this.formData.recentProject.projectImage = downloadURL;
        }
        fileState.selectedFile = null;
        fileState.isUploading = false;
      });

      return downloadURL;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to upload ${fileType}`;

      runInAction(() => {
        fileState.uploadError = errorMessage;
        fileState.isUploading = false;
      });

      return null;
    }
  };

  uploadAllFiles = async (): Promise<boolean> => {
    const regularUploadPromises = Object.entries(this.documents)
      .filter(([_, fileState]) => {
        const hasFile = fileState.selectedFile !== null;
        return hasFile;
      })
      .map(([fileType]) => {
        return this.uploadFile(fileType as FileType);
      });

    const verificationUploadPromises = Array.from(this.verificationDocuments.keys())
      .filter(fileName => {
        const hasFile = this.verificationDocuments.get(fileName)?.selectedFile !== null;
        return hasFile;
      })
      .map(async (fileName) => {
        try {
          const docState = this.verificationDocuments.get(fileName);
          if (!docState?.selectedFile) {
            return { fileName, url: null };
          }

          runInAction(() => {
            docState.isUploading = true;
            docState.uploadError = null;
          });

          // Delete old verification file before uploading new one
          await this.deleteOldVerificationFile(fileName);

          const actualFileName = docState.selectedFile.name;
          const storagePath = this.getStoragePath('document', actualFileName);
          const fileRef = ref(storage, storagePath);
          const snapshot = await uploadBytes(fileRef, docState.selectedFile);
          const downloadURL = await getDownloadURL(snapshot.ref);

          runInAction(() => {
            docState.selectedFile = null;
            docState.isUploading = false;
          });

          return { fileName, url: downloadURL };
        } catch (error) {
          const docState = this.verificationDocuments.get(fileName);
          if (docState) {
            runInAction(() => {
              docState.uploadError = error instanceof Error ? error.message : 'Upload failed';
              docState.isUploading = false;
            });
          }

          return { fileName, url: null };
        }
      });

    const allPromises = [...regularUploadPromises, ...verificationUploadPromises];

    if (allPromises.length === 0) {

      return true;
    }

    try {
      const results = await Promise.all(allPromises);

      const hasFailures = results.some(result => {
        const isFailed = result === null || (typeof result === 'object' && result.url === null);

        return isFailed;
      });

      if (!hasFailures) {
        results.forEach(result => {
          if (result && typeof result === 'object' && 'fileName' in result && 'url' in result && result.fileName && result.url) {
            runInAction(() => {
              if (!this.formData.verification) {
                this.formData.verification = {
                  linkedInProfileUrl: '',
                  verificationCV: '',
                  verificationCvDescription: '',
                  verificationDocument: '',
                  verificationDocumentDescription: '',
                };
              }

              if (result.fileName === 'verificationCV') {
                this.formData.verification.verificationCV = result.url || '';
              } else if (result.fileName === 'verificationDocument') {
                this.formData.verification.verificationDocument = result.url || '';
              }
            });
          }
        });
      }

      return !hasFailures;
    } catch (error) {
      return false;
    }
  };

  validateForm = (): OnboardingErrors => {
    const newErrors: OnboardingErrors = {};

    // Required string fields with validation rules
    const requiredStringFields: Array<{
      field: keyof OnboardingData;
      message: string;
      validateText?: boolean; // Whether to apply text input validation
      isTextField?: boolean; // Whether it's a text field that allows punctuation
      nested?: string; // For nested object fields
    }> = [
      { field: 'firstName', message: 'First name is required', validateText: true },
      { field: 'lastName', message: 'Last name is required', validateText: true },
      { field: 'gender', message: 'Please select your gender' },
      { field: 'city', message: 'City is required', validateText: true },
      { field: 'about', message: 'Please tell us about your motivation', validateText: true, isTextField: true },
      { field: 'phone', message: 'Phone number is required' },
      { field: 'skills', message: 'Please add at least one skill' },
      { field: 'degrees', message: 'Please add at least one qualification' },
    ];

    // Validate basic required fields
    requiredStringFields.forEach(({ field, message, validateText, isTextField }) => {
      const value = this.formData[field];

      // Check if field is empty
      if (Array.isArray(value)) {
        if (!value.length) {
          (newErrors as any)[field] = message;
        }
      } else if (!value?.toString().trim()) {
        (newErrors as any)[field] = message;
      } else if (validateText) {
        const isValid = isTextField
          ? this.isValidTextFieldInput(value.toString())
          : this.isValidTextInput(value.toString());

        if (!isValid) {
          const allowedChars = isTextField
            ? 'Only letters and numbers are allowed'
            : 'Only letters and numbers are allowed';
          (newErrors as any)[field] = allowedChars;
        }
      }
    });

    // Special validation for phone number
    if (this.formData.phone.trim() && !isValidPhoneNumber(this.formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Special validation for about field length
    if (this.formData.about?.trim() && this.formData.about.length < 20) {
      newErrors.about = 'About section must be at least 20 characters';
    } else if (this.formData.about?.trim() && this.formData.about.length > 80) {
      newErrors.about = 'About section must be at most 80 characters';
    }

    // Special validation for role preferences
    if (this.formData.role === UserRole.Founder) {
      if (!this.formData.target_role) {
        newErrors.target_role = 'Please select a target role';
      }
    } else if (this.formData.role === UserRole.SoftwareEngineer) {
      if (!this.formData.project_stage) {
        newErrors.project_stage = 'Please select a project stage';
      }
    }

    if (!this.formData.timeframe) {
      newErrors.timeframe = 'Please select your preferred commitment';
    }

    if (!this.formData.compensation) {
      newErrors.compensation = 'Please select your compensation preference';
    }

    if (!this.formData.longDescription || this.formData.longDescription.trim().length < 100) {
      newErrors.longDescription = 'Description must be at least 100 characters';
    } else if (this.formData.longDescription.length > 10000) {
      newErrors.longDescription = 'Description must be at most 10000 characters';
    }

    // Profile image validation
    const hasProfileImageUrl = this.formData.profileImageUrl?.trim();
    const hasSelectedProfileImage = this.documents.profileImage.selectedFile;
    const profileImageUploadError = this.documents.profileImage.uploadError;

    if (!hasProfileImageUrl && !hasSelectedProfileImage) {
      newErrors.profileImageUrl = 'Profile image is required';
    } else if (profileImageUploadError && !hasProfileImageUrl) {
      // Only block if there's an upload error AND no existing valid image
      newErrors.profileImageUrl = profileImageUploadError;
    }

    // Project validation
    // Project name must be 2-50 characters with letters, numbers and spaces only.
    const projectErrors = this.validateProjectFields();
    if (Object.keys(projectErrors).length > 0) {
      newErrors.recentProject = projectErrors;
    }

    // Verification fields validation
    const verificationErrors = this.validateVerificationFields();
    Object.assign(newErrors, verificationErrors);

    return newErrors;
  };

  // Separate method for project validation
  private validateProjectFields = (): Partial<Record<keyof RecentProject, string>> => {
    const projectErrors: Partial<Record<keyof RecentProject, string>> = {};
    const project = this.formData.recentProject;

    // Required project fields
    const requiredProjectFields = [
      { field: 'name' as keyof RecentProject, message: 'Project name is required', validateText: true, isTextField: false },
      { field: 'description' as keyof RecentProject, message: 'Project description is required', validateText: true, isTextField: true },
    ];

    requiredProjectFields.forEach(({ field, message, validateText, isTextField }) => {
      const value = project[field];
      if (!value?.toString().trim()) {
        projectErrors[field] = message;
      } else if (validateText) {
        const isValid = isTextField
          ? this.isValidTextFieldInput(value.toString())
          : this.isValidTextInput(value.toString());

        if (!isValid) {
          const allowedChars = isTextField
            ? 'Only letters and numbers are allowed'
            : 'Only letters, numbers and spaces are allowed';
          projectErrors[field] = allowedChars;
        }
      }
    });

    // Project image validation
    const hasProjectImageUrl = project.projectImage?.trim();
    const hasSelectedProjectImage = this.documents.projectImage.selectedFile;
    const projectImageUploadError = this.documents.projectImage.uploadError;

    if (!hasProjectImageUrl && !hasSelectedProjectImage) {
      projectErrors.projectImage = 'Project image is required';
    } else if (projectImageUploadError && !hasProjectImageUrl) {
      // Only block if there's an upload error AND no existing valid image
      projectErrors.projectImage = projectImageUploadError;
    }

    // Project name length validation
    const projectName = project.name;
    if (projectName?.trim()) {
      if (projectName.length < 2 || projectName.length > 50) {
        projectErrors.name = 'Project name must be 2-50 characters with letters, numbers and spaces only';
      }
    }

    // Project description length validation
    const description = project.description;
    if (description?.trim()) {
      if (description.length < 100) {
        projectErrors.description = 'Project description must be at least 100 characters';
      } else if (description.length > 500) {
        projectErrors.description = 'Project description must be at most 500 characters';
      }
    }

    // Role-specific validations
    if (this.formData.role === UserRole.SoftwareEngineer) {
      if (!project.techStack?.length) {
        projectErrors.techStack = 'Please add at least one technology to your project tech stack';
      }
      if (!project.developerTechStack?.length) {
        projectErrors.developerTechStack = 'Please add at least one technology to your developer tech stack';
      }
    } else if (this.formData.role === UserRole.Founder) {
      if (!project.projectCategory?.length) {
        projectErrors.projectCategory = 'Please select at least one project category';
      } else if (project.projectCategory.length > 3) {
        projectErrors.projectCategory = 'You can select up to 3 project categories';
      }
    }

    return projectErrors;
  };

  // Separate method for verification fields validation
  private validateVerificationFields = (): Partial<OnboardingErrors> => {
    const errors: Partial<OnboardingErrors> = {};
    const verification = this.formData.verification;

    // Determine if both LinkedIn and CV are required
    const jobPosition = this.formData.webOnboarding?.appliedFor;
    const isAppliedForPosition = jobPosition ? jobPosition.length > 0 : false;
    const requiresBothLinkedInAndCV = isAppliedForPosition;

    // LinkedIn URL validation
    const linkedInUrl = verification?.linkedInProfileUrl?.trim() || '';
    const linkedInPrefix = 'https://www.linkedin.com/in/';
    const isLinkedInEmpty = !linkedInUrl || linkedInUrl === linkedInPrefix;
    const isLinkedInValid = linkedInUrl && linkedInUrl !== linkedInPrefix && /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/.test(linkedInUrl);

    // CV validation
    const cvUrl = verification?.verificationCV?.trim() || '';
    const hasPendingCV = this.verificationDocuments.has('verificationCV') &&
      this.verificationDocuments.get('verificationCV')?.selectedFile !== null;
    const cvUploadError = this.verificationDocuments.get('verificationCV')?.uploadError;
    const hasCv = cvUrl || hasPendingCV;

    if (requiresBothLinkedInAndCV) {
      // Software Engineer who applied for a position
      if (!linkedInUrl) {
        errors.linkedInProfileUrl = 'LinkedIn profile URL is required';
      } else if (!isLinkedInValid) {
        errors.linkedInProfileUrl = 'Please enter a valid LinkedIn profile URL';
      }

      if (!cvUrl && !hasPendingCV) {
        errors.verificationCV = 'Please upload your CV';
      } else if (cvUploadError && !cvUrl) {
        errors.verificationCV = cvUploadError;
      }

      // CV description validation (required when CV is uploaded)
      const cvDescription = verification?.verificationCvDescription?.trim() || '';
      if (hasCv && !cvDescription) {
        errors.verificationCvDescription = 'Please provide a description for your CV';
      }
    } else {
      // Founder OR Software Engineer who didn't apply for a certain position
      const cvDescription = verification?.verificationCvDescription?.trim() || '';
      const hasCvWithDescription = hasCv && cvDescription;

      // Check if user has provided at least one valid option
      const hasValidLinkedIn = isLinkedInValid;
      const hasValidCV = hasCvWithDescription;

      if (!hasValidLinkedIn && !hasValidCV) {
        // Neither valid LinkedIn nor CV with description provided
        if (!hasCv && isLinkedInEmpty) {
          // Nothing provided at all - show error only on LinkedIn field
          errors.linkedInProfileUrl = 'Please provide your LinkedIn or upload the CV';
        } else if (hasCv && !cvDescription) {
          // CV uploaded but missing description - only show CV description error
          errors.verificationCvDescription = 'Please provide a description for your CV';
        } else if (!isLinkedInEmpty && !isLinkedInValid) {
          // Invalid LinkedIn format (user tried to fill it but it's invalid)
          errors.linkedInProfileUrl = 'Please enter a valid LinkedIn profile URL';
        }

        // Show CV upload error if exists
        if (cvUploadError && !cvUrl) {
          errors.verificationCV = cvUploadError;
        }
      } else {
        // User has provided at least one valid option - only validate what they provided

        // Validate LinkedIn if provided (and not just the prefix)
        if (!isLinkedInEmpty && !isLinkedInValid) {
          errors.linkedInProfileUrl = 'Please enter a valid LinkedIn profile URL';
        }

        // Validate CV if uploaded
        if (hasCv) {
          if (!cvDescription) {
            errors.verificationCvDescription = 'Please provide a description for your CV';
          }
          if (cvUploadError && !cvUrl) {
            errors.verificationCV = cvUploadError;
          }
        }
      }
    }

    return errors;
  };
}
